"use client";

import React from "react";
import { Server, Cpu, Activity, Shield, LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
    Cpu,
    Server,
    Activity,
    Shield
};

export interface ValueCard {
    iconName: string;
    title: string;
    desc: string;
    footer: string;
}

export interface CtlValuesBlockProps {
    tag?: string;
    title?: string;
    description?: string;
    values?: ValueCard[];
}

export default function CtlValuesBlock({
    tag = "05 / CORE VALUES",
    title = "Design & Brand Values",
    description = "We mapped the website architectural details across four primary pillars, directly referencing CTL's commitment to quality assurance and system expertise.",
    values = [
        {
            iconName: "Cpu",
            title: "QUALITY ASSURANCE",
            desc: "We institute ISO 9001 - 2015 certified standards, ensuring every design and installation is quality assured and result oriented.",
            footer: "// QUALITY ASSURANCE"
        },
        {
            iconName: "Server",
            title: "EXPERIENCE",
            desc: "Rendering legacy collaborations since 1994, our 25+ years of experience establishes solid credibility in IT infrastructure.",
            footer: "// ESTABLISHED LEGACY"
        },
        {
            iconName: "Activity",
            title: "GLOBAL STANDARDS",
            desc: "Certified by major OEMs and global standards of data centre certifications (CDCP, CDCS, CDCE) to support client-centric needs.",
            footer: "// GLOBAL STANDARDS"
        },
        {
            iconName: "Shield",
            title: "CUSTOMISATION",
            desc: "Delivering highly researched, customised technological solutions designed specifically around your business operations.",
            footer: "// CUSTOMISED EXPERTISE"
        }
    ]
}: CtlValuesBlockProps) {
    return (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-white border-b border-[#EAEAEA] text-[#444] font-sans antialiased w-full">
            <div className="container mx-auto">
                
                {/* Section header */}
                <div className="max-w-2xl mb-16">
                    {tag && (
                        <span className="text-xs font-mono text-[#44AA52] tracking-widest block mb-2 uppercase font-semibold">
                            {tag}
                        </span>
                    )}
                    {title && (
                        <h2 className="text-4xl font-light text-[#1A1A1A] leading-tight">
                            {title}
                        </h2>
                    )}
                    {description && (
                        <p className="text-zinc-500 text-sm mt-3 font-light max-w-lg">
                            {description}
                        </p>
                    )}
                </div>

                {/* Values Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {values && values.map((val, idx) => {
                        const Icon = iconMap[val.iconName] || Cpu;
                        return (
                            <div key={idx} className="bg-[#FAFBF9] border border-[#E5ECE5] rounded-2xl p-8 flex flex-col justify-between space-y-6 group hover:border-[#44AA52]/30 transition-all duration-500 shadow-xs">
                                <div className="space-y-4">
                                    <div className="p-3 bg-[#44AA52]/10 border border-[#44AA52]/20 rounded-xl w-fit text-[#44AA52]">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-xl font-medium text-[#1A1A1A]">{val.title}</h3>
                                    <p className="text-zinc-650 text-xs leading-relaxed font-light">
                                        {val.desc}
                                    </p>
                                </div>
                                <span className="text-[10px] font-mono text-zinc-450 font-medium">
                                    {val.footer}
                                </span>
                            </div>
                        );
                    })}
                </div>

            </div>
        </section>
    );
}
